import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatMoney } from "helpers/numberHelper";
import { formatToFrenchDate } from "utils/dateUtils";
import { getLabelPaymentMethod } from "utils/stringUtils";

const FeeInvoice = ({ data }: { data: any }) => {
  const { t } = useTranslation();

  const totalPaid = useMemo(
    () =>
      data?.feePayments?.reduce(
        (sum: number, payment: any) => sum + parseFloat(payment.amount),
        0
      ) ?? 0,
    [data]
  );

  const remaining = useMemo(
    () =>
      data ? data.amountNotPaid ?? Math.max(data.amount - totalPaid, 0) : 0,
    [data, totalPaid]
  );

  const student = data?.student;
  const studentUser = student?.user;

  const studentFullName = [
    studentUser?.firstName,
    studentUser?.lastName,
  ].filter(Boolean).join(" ");

  const handleDownload = () => {
    window.print(); 
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Top actions (print / download) */}
        <div className="row mb-4 d-print-none">
          <div className="col-12 d-flex justify-content-end gap-2">
            <button className="btn btn-primary" onClick={handleDownload}>
              <i className="fas fa-print me-2" />
              {t("download")}
            </button>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="card shadow-sm invoice-card">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-start mb-4">
                  <div>
                    <h3 className="mb-1">{t("fee_invoice")}</h3>
                  </div>

                  <div className="mt-3 mt-md-0 text-md-end small">
                    <div className="mb-1">
                      <span className="text-muted me-1">
                        {t("invoice_no")}:
                      </span>
                      <span className="fw-semibold">
                        #{data?.id?.slice(0, 8)}
                      </span>
                    </div>
                    <div className="mb-1">
                      <span className="text-muted me-1">
                        {t("fee_type")}:
                      </span>
                      <span className="fw-semibold">
                        {data?.feeType?.name}
                      </span>
                    </div>
                    <div className="mb-1">
                      <span className="text-muted me-1">
                        {t("due_date")}:
                      </span>
                      <span>{formatToFrenchDate(data?.dueDate)}</span>
                    </div>
                    <div className="mt-2">
                      <span className="text-muted me-1">
                        {t("payment_status")}:
                      </span>
                      <span className="badge bg-light text-dark text-capitalize">
                        {t(`${data?.status}`)}
                      </span>
                    </div>
                  </div>
                </div>

                <hr className="my-4" />

                {student && (
                  <div className="mb-4">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-2 d-flex justify-content-between">
                          <span className="text-muted">
                            {t("student")}:
                          </span>
                          <span className="fw-semibold ms-2">
                            {studentFullName || "—"}
                          </span>
                        </div>

                        {student?.massarCode && (
                          <div className="mb-2 d-flex justify-content-between">
                            <span className="text-muted">
                              {t("massar_code")}
                            </span>
                            <span className="fw-semibold ms-2">
                              {student.massarCode}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="col-md-6">
                        {student?.class && (
                          <div className="mb-2 d-flex justify-content-between">
                            <span className="text-muted">
                              {t("class")}
                            </span>
                            <span className="fw-semibold ms-2">
                              {student.class?.name}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <h6 className="text-uppercase text-muted small mb-3">
                    {t("payment_history")}
                  </h6>

                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("payment_date")}</th>
                          <th>{t("payment_method")}</th>
                          <th className="text-end">{t("amount")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data?.feePayments?.length ? (
                          data.feePayments.map((payment: any) => (
                            <tr key={payment.id}>
                              <td>{formatToFrenchDate(payment.createdAt)}</td>
                              <td>
                                {getLabelPaymentMethod(
                                  payment.paymentMethod?.name || ""
                                )}
                              </td>
                              <td className="text-end">
                                {formatMoney(payment.amount)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={3} className="text-center text-muted">
                              {t("no_payments_yet")}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="row justify-content-end mb-4">
                  <div className="col-md-6 col-lg-4">
                    <div className="border rounded p-3 bg-light-subtle">
                      <div className="d-flex justify-content-between mb-2">
                        <span>{t("total_amount")}</span>
                        <span className="fw-semibold">
                          {formatMoney(data?.amount)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>{t("amount_paid")}</span>
                        <span className="fw-semibold">
                          {formatMoney(totalPaid)}
                        </span>
                      </div>
                      <div
                        className={
                          "d-flex justify-content-between pt-2 mt-1 border-top " +
                          (remaining > 0 ? "text-warning" : "text-success")
                        }
                      >
                        <span className="fw-semibold">
                          {t("amount_remaining")}
                        </span>
                        <span className="fw-semibold">
                          {formatMoney(remaining)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-top pt-4">
                  <div className="text-center mb-2">
                    <span className="text-dark fw-medium d-block">
                      {t("terms_conditions")}:
                    </span>
                  </div>
                  <p className="text-center mb-3">
                    {t("invoice_terms")}
                  </p>
                  <p className="text-center mb-0 fw-semibold">
                    {t("thank_you")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeeInvoice;
